import React, { useEffect, useMemo, useState } from 'react';
import {
    Award,
    Camera,
    CheckCircle2,
    Gauge,
    PlayCircle,
    Route as RouteIcon,
    Users,
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { Profile } from '../../../types';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';

interface ParadaOp {
    id: string;
    status: string;
    foto_url?: string | null;
    visitado_em?: string | null;
}

interface RotaOp {
    id: string;
    data: string;
    status: string;
    nome?: string | null;
    vendedor_id: string;
    distancia_total_km?: number | null;
    rota_paradas: ParadaOp[];
}

type Periodo = 'hoje' | 'ontem' | '7d' | '14d' | '30d' | '90d' | 'custom';

const PERIODOS: Array<{ value: Periodo; label: string }> = [
    { value: 'hoje', label: 'Hoje' },
    { value: 'ontem', label: 'Ontem' },
    { value: '7d', label: '7 dias' },
    { value: '14d', label: '14 dias' },
    { value: '30d', label: '30 dias' },
    { value: '90d', label: '90 dias' },
    { value: 'custom', label: 'Personalizado' },
];

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const rangeDoPeriodo = (p: Periodo, customIni: string, customFim: string): [string, string] => {
    const hoje = new Date();
    switch (p) {
        case 'hoje': return [iso(hoje), iso(hoje)];
        case 'ontem': { const d = new Date(hoje); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; }
        case '7d': { const d = new Date(hoje); d.setDate(d.getDate() - 6); return [iso(d), iso(hoje)]; }
        case '14d': { const d = new Date(hoje); d.setDate(d.getDate() - 13); return [iso(d), iso(hoje)]; }
        case '30d': { const d = new Date(hoje); d.setDate(d.getDate() - 29); return [iso(d), iso(hoje)]; }
        case '90d': { const d = new Date(hoje); d.setDate(d.getDate() - 89); return [iso(d), iso(hoje)]; }
        case 'custom': return [customIni || iso(hoje), customFim || iso(hoje)];
    }
};

const fmtNum = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const fmtPct = (v: number) => `${v.toFixed(1)}%`;

// Agrupamento de status de rota
const ehConcluida = (s: string) => s === 'concluida';
const ehEmExecucao = (s: string) => s === 'em_execucao';
const ehCancelada = (s: string) => s === 'cancelada';
const ehAgendada = (s: string) => ['planejada', 'agendada', 'reagendada', 'pendente'].includes(s);

const Kpi: React.FC<{ label: string; value: string; icon: React.ReactNode; tone?: string; hint?: string }> = ({ label, value, icon, tone = 'text-gray-900 dark:text-white', hint }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4" title={hint}>
        <div className="flex items-start justify-between">
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
            <span className="text-gray-300 dark:text-gray-600">{icon}</span>
        </div>
        <p className={`text-xl font-bold mt-1 ${tone}`}>{value}</p>
    </div>
);

interface OperacionalDashboardProps {
    profile: Profile | null;
    profiles: Profile[];
    vendedorSel: string; // '' = todos os operadores
}

/**
 * Dashboard Gerencial Operacional (Motoboy M4): visão de execução de campo com recorte
 * por papel motoboy — rotas criadas/concluídas/em andamento, fotos capturadas,
 * operadores ativos e ranking de produtividade dos operadores.
 */
const OperacionalDashboard: React.FC<OperacionalDashboardProps> = ({ profile, profiles, vendedorSel }) => {
    const [periodo, setPeriodo] = useState<Periodo>('30d');
    const [customIni, setCustomIni] = useState(iso(new Date()));
    const [customFim, setCustomFim] = useState(iso(new Date()));
    const [rotas, setRotas] = useState<RotaOp[]>([]);
    const [loading, setLoading] = useState(true);

    const [ini, fim] = rangeDoPeriodo(periodo, customIni, customFim);

    const motoboyIds = useMemo(() => profiles.filter(p => p.role === 'motoboy').map(p => String(p.id)), [profiles]);
    const semMotoboys = motoboyIds.length === 0 && !vendedorSel;

    useEffect(() => {
        const fetchRotas = async () => {
            if (!profile) return;
            setLoading(true);
            try {
                let query = supabase
                    .from('rotas_comerciais')
                    .select('id, data, status, nome, vendedor_id, distancia_total_km, rota_paradas(id, status, foto_url, visitado_em)')
                    .gte('data', ini)
                    .lte('data', fim);
                if (vendedorSel) query = query.eq('vendedor_id', vendedorSel);
                else if (motoboyIds.length > 0) query = query.in('vendedor_id', motoboyIds);
                const { data, error } = await query;
                if (error) throw error;
                setRotas((data || []) as unknown as RotaOp[]);
            } catch (err) {
                console.error('Erro ao carregar dashboard operacional:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchRotas();
    }, [profile?.id, vendedorSel, ini, fim, motoboyIds.join(',')]);

    const nome = (id?: string | null) => profiles.find(p => String(p.id) === String(id))?.full_name?.split(' ').slice(0, 2).join(' ') || '—';

    const stats = useMemo(() => {
        let concluidas = 0, emExecucao = 0, agendadas = 0, canceladas = 0;
        let fotos = 0, visitadas = 0, paradasTotal = 0, km = 0;
        const operadores = new Set<string>();
        const porDia: Record<string, { concluidas: number; criadas: number }> = {};
        const porOperador: Record<string, { criadas: number; concluidas: number; emExecucao: number; paradas: number; visitadas: number; fotos: number; km: number }> = {};
        const op = (id: string) => (porOperador[id] ||= { criadas: 0, concluidas: 0, emExecucao: 0, paradas: 0, visitadas: 0, fotos: 0, km: 0 });

        for (const r of rotas) {
            operadores.add(r.vendedor_id);
            const o = op(r.vendedor_id);
            o.criadas++;
            const dia = r.data;
            const pd = (porDia[dia] ||= { concluidas: 0, criadas: 0 });
            if (!ehCancelada(r.status)) pd.criadas++;

            if (ehConcluida(r.status)) { concluidas++; o.concluidas++; pd.concluidas++; }
            else if (ehEmExecucao(r.status)) { emExecucao++; o.emExecucao++; }
            else if (ehCancelada(r.status)) { canceladas++; }
            else if (ehAgendada(r.status)) { agendadas++; }

            if (ehConcluida(r.status) || ehEmExecucao(r.status)) { km += Number(r.distancia_total_km || 0); o.km += Number(r.distancia_total_km || 0); }

            for (const p of r.rota_paradas) {
                paradasTotal++; o.paradas++;
                if (p.status === 'visitado') { visitadas++; o.visitadas++; }
                if (p.foto_url) { fotos++; o.fotos++; }
            }
        }

        const criadas = rotas.filter(r => !ehCancelada(r.status)).length;
        const taxaConclusao = criadas > 0 ? (concluidas / criadas) * 100 : 0;
        const taxaVisita = paradasTotal > 0 ? (visitadas / paradasTotal) * 100 : 0;

        const ranking = Object.entries(porOperador)
            .map(([id, v]) => ({
                id, ...v,
                taxaConcl: v.criadas > 0 ? (v.concluidas / v.criadas) * 100 : 0,
            }))
            .sort((a, b) => b.fotos - a.fotos || b.visitadas - a.visitadas || b.concluidas - a.concluidas);

        const dias = Object.entries(porDia).sort((a, b) => b[0].localeCompare(a[0])).slice(0, 14);

        return {
            criadas, concluidas, emExecucao, agendadas, canceladas,
            fotos, visitadas, paradasTotal, km, taxaConclusao, taxaVisita,
            operadores: operadores.size, ranking, dias,
        };
    }, [rotas]);

    const maxDia = Math.max(1, ...stats.dias.map(([, d]) => d.criadas));
    const totalStatus = Math.max(1, stats.agendadas + stats.emExecucao + stats.concluidas + stats.canceladas);
    const segStatus = [
        { label: 'Agendadas', valor: stats.agendadas, cor: 'bg-blue-400', txt: 'text-blue-600 dark:text-blue-400' },
        { label: 'Em Execução', valor: stats.emExecucao, cor: 'bg-amber-400', txt: 'text-amber-600 dark:text-amber-400' },
        { label: 'Concluídas', valor: stats.concluidas, cor: 'bg-emerald-500', txt: 'text-emerald-600 dark:text-emerald-400' },
        { label: 'Canceladas', valor: stats.canceladas, cor: 'bg-gray-400', txt: 'text-gray-500 dark:text-gray-400' },
    ];

    return (
        <div className="space-y-4">
            {/* Filtros */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-3 flex flex-wrap items-center gap-2">
                <RouteIcon size={15} className="text-gray-400 ml-1" />
                <span className="text-xs font-bold text-gray-700 dark:text-gray-200 mr-1">
                    Operação de Campo {vendedorSel ? `· ${nome(vendedorSel)}` : '· Operadores (Motoboys)'}
                </span>
                <div className="flex items-center gap-1 flex-wrap ml-auto">
                    {PERIODOS.map(p => (
                        <button
                            key={p.value}
                            onClick={() => setPeriodo(p.value)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${periodo === p.value
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {p.label}
                        </button>
                    ))}
                    {periodo === 'custom' && (
                        <span className="flex items-center gap-1.5 ml-1">
                            <input type="date" value={customIni} onChange={e => setCustomIni(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                            <span className="text-xs text-gray-400">→</span>
                            <input type="date" value={customFim} onChange={e => setCustomFim(e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 focus:outline-none" />
                        </span>
                    )}
                </div>
            </div>

            {semMotoboys && (
                <div className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                    Nenhum operador com papel <b>Motoboy</b> cadastrado ainda — exibindo todas as rotas de campo. Atribua o papel em Config → Usuários para recortar a operação.
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16"><LoadingSpinner label="Carregando operação..." size="sm" /></div>
            ) : (
                <>
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Kpi label="Rotas Criadas" value={fmtNum(stats.criadas)} icon={<RouteIcon size={18} />} hint="Rotas do período (exceto canceladas)" />
                        <Kpi label="Concluídas" value={fmtNum(stats.concluidas)} icon={<CheckCircle2 size={18} />} tone="text-emerald-600 dark:text-emerald-400" hint="Rotas finalizadas pelo operador" />
                        <Kpi label="Em Andamento" value={fmtNum(stats.emExecucao)} icon={<PlayCircle size={18} />} tone="text-amber-500" hint="Rotas em execução agora" />
                        <Kpi label="Taxa de Conclusão" value={fmtPct(stats.taxaConclusao)} icon={<Gauge size={18} />} tone={stats.taxaConclusao >= 70 ? 'text-emerald-600 dark:text-emerald-400' : stats.taxaConclusao >= 40 ? 'text-amber-500' : 'text-red-500'} hint="Concluídas ÷ criadas" />
                        <Kpi label="Fotos Capturadas" value={fmtNum(stats.fotos)} icon={<Camera size={18} />} tone="text-blue-600 dark:text-blue-400" hint="Medidores fotografados no período" />
                        <Kpi label="Operadores Ativos" value={fmtNum(stats.operadores)} icon={<Users size={18} />} tone="text-purple-600 dark:text-purple-400" hint="Operadores com ao menos uma rota no período" />
                    </div>

                    {/* Status das rotas + Rotas por dia */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Status das Rotas</h2>
                            <div className="flex h-3 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700 mb-3">
                                {segStatus.map(s => s.valor > 0 && (
                                    <div key={s.label} className={s.cor} style={{ width: `${(s.valor / totalStatus) * 100}%` }} title={`${s.label}: ${s.valor}`}></div>
                                ))}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {segStatus.map(s => (
                                    <div key={s.label} className="flex items-center gap-2">
                                        <span className={`w-2.5 h-2.5 rounded-full ${s.cor}`}></span>
                                        <span className="text-xs text-gray-600 dark:text-gray-300 flex-1">{s.label}</span>
                                        <span className={`text-xs font-bold ${s.txt}`}>{s.valor}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex flex-wrap gap-2">
                                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Paradas visitadas: <b>{fmtNum(stats.visitadas)}/{fmtNum(stats.paradasTotal)}</b></span>
                                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Execução das paradas: <b>{fmtPct(stats.taxaVisita)}</b></span>
                                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">Km percorrida: <b>{fmtNum(stats.km)} km</b></span>
                            </div>
                        </div>

                        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Rotas por Dia</h2>
                            {stats.dias.length === 0 ? <p className="text-xs text-gray-400">Sem rotas no período.</p> : (
                                <div className="space-y-1.5">
                                    {stats.dias.map(([dia, d]) => (
                                        <div key={dia} className="flex items-center gap-2">
                                            <span className="text-[11px] text-gray-500 dark:text-gray-400 w-12 shrink-0">{new Date(dia + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}</span>
                                            <div className="flex-1 h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden relative">
                                                <div className="h-full bg-blue-300 dark:bg-blue-500/40 rounded-full absolute inset-y-0 left-0" style={{ width: `${(d.criadas / maxDia) * 100}%` }} title={`${d.criadas} criadas`}></div>
                                                <div className="h-full bg-emerald-500 rounded-full absolute inset-y-0 left-0" style={{ width: `${(d.concluidas / maxDia) * 100}%` }} title={`${d.concluidas} concluídas`}></div>
                                            </div>
                                            <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 w-12 text-right">{d.concluidas}/{d.criadas}</span>
                                        </div>
                                    ))}
                                    <div className="flex items-center gap-3 pt-1.5 text-[10px] text-gray-400">
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-300 dark:bg-blue-500/40"></span> Criadas</span>
                                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"></span> Concluídas</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Ranking de Produtividade dos Operadores */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                            <Award size={15} className="text-amber-500" /> Ranking de Produtividade dos Operadores
                        </h2>
                        {stats.ranking.length === 0 ? (
                            <p className="text-xs text-gray-400">Nenhum operador com rotas no período.</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                            <th className="py-2 pr-3 font-semibold">Operador</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Rotas</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Conclusão</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Visitas</th>
                                            <th className="py-2 pr-3 font-semibold text-right">Fotos</th>
                                            <th className="py-2 font-semibold text-right">Km</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stats.ranking.map((r, i) => (
                                            <tr key={r.id} className="border-b border-gray-50 dark:border-gray-700/50">
                                                <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">
                                                    <span className="flex items-center gap-2">
                                                        <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white shrink-0 ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                                        {nome(r.id)}
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.concluidas}/{r.criadas}</td>
                                                <td className={`py-2 pr-3 text-right font-bold ${r.taxaConcl >= 70 ? 'text-emerald-600 dark:text-emerald-400' : r.taxaConcl >= 40 ? 'text-amber-500' : 'text-red-500'}`}>{r.criadas > 0 ? fmtPct(r.taxaConcl) : '—'}</td>
                                                <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{r.visitadas}/{r.paradas}</td>
                                                <td className="py-2 pr-3 text-right font-bold text-blue-600 dark:text-blue-400">{r.fotos}</td>
                                                <td className="py-2 text-right text-gray-600 dark:text-gray-300">{fmtNum(r.km)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Configuração do alerta de fim de expediente (Motoboy v3, item 3) */}
                    {['adm', 'gerente'].includes(profile?.role || '') && <ConfigExpedienteCard />}
                </>
            )}
        </div>
    );
};

/**
 * Horário do fim de expediente + janela de escalonamento ao líder.
 * Grava em system_settings (rotas_fim_expediente / rotas_alerta_lider_min);
 * o cron verificar_rotas_nao_concluidas e o app do operador leem daqui.
 */
const ConfigExpedienteCard: React.FC = () => {
    const { profile: profileConfig } = useAuth();
    const [horario, setHorario] = useState('18:00');
    const [janelaMin, setJanelaMin] = useState('60');
    const [salvando, setSalvando] = useState(false);
    const [salvo, setSalvo] = useState(false);

    useEffect(() => {
        const carregar = async () => {
            const { data } = await supabase.from('system_settings').select('key, value')
                .in('key', ['rotas_fim_expediente', 'rotas_alerta_lider_min']);
            const fim = data?.find(r => r.key === 'rotas_fim_expediente')?.value;
            const janela = data?.find(r => r.key === 'rotas_alerta_lider_min')?.value;
            if (fim) setHorario(fim);
            if (janela) setJanelaMin(janela);
        };
        carregar();
    }, []);

    const salvar = async () => {
        setSalvando(true);
        setSalvo(false);
        try {
            const { error } = await supabase.from('system_settings').upsert([
                { key: 'rotas_fim_expediente', value: horario },
                { key: 'rotas_alerta_lider_min', value: janelaMin },
            ], { onConflict: 'key' });
            if (error) throw error;
            logUsageEvent({ userId: profileConfig?.id, modulo: 'crm', funcionalidade: 'rotas', acao: 'salvar_config_expediente', origem: 'acao' });
            setSalvo(true);
            setTimeout(() => setSalvo(false), 2500);
        } catch (err) {
            console.error('Erro ao salvar configuração de expediente:', err);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
                <Gauge size={15} className="text-amber-500" /> Alerta de Fim de Expediente
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Após este horário, rotas do dia com visitas pendentes alertam o operador; sem ação na janela abaixo, o Líder Comercial é notificado.
            </p>
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-300 block mb-1">Fim do expediente</label>
                    <input
                        type="time"
                        value={horario}
                        onChange={e => setHorario(e.target.value)}
                        className="px-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
                <div>
                    <label className="text-[11px] font-bold text-gray-600 dark:text-gray-300 block mb-1">Notificar líder após (min)</label>
                    <input
                        type="number"
                        min={15}
                        step={15}
                        value={janelaMin}
                        onChange={e => setJanelaMin(e.target.value)}
                        className="w-28 px-3 py-2 text-sm rounded-xl bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                </div>
                <button
                    onClick={salvar}
                    disabled={salvando || !horario}
                    className="px-4 py-2 text-xs font-bold rounded-xl bg-primary text-white hover:opacity-90 transition-opacity disabled:opacity-40"
                >
                    {salvando ? 'Salvando...' : salvo ? 'Salvo ✓' : 'Salvar'}
                </button>
            </div>
        </div>
    );
};

export default OperacionalDashboard;
